import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { COMPANY } from "../../lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1587919968590-fbc98cea6c9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtaW5pbmclMjBleGNhdmF0b3J8ZW58MHx8fHwxNzg1Mzg3NzY4fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["ANGMISH", "GENERAL", "DEALERS"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 * i + 0.2 },
  }),
};

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grain flex flex-col justify-end"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0" aria-hidden="true">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[2] bg-black" aria-hidden="true" />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-background via-background/20 to-transparent" aria-hidden="true" />

      {/* Overline top */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.8 }}
        className="absolute top-24 md:top-28 left-0 right-0 z-10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-primary">
          <span>Zambia Nationwide</span>
          <span className="hidden sm:block text-muted-foreground">Est. Lusaka</span>
        </div>
      </motion.div>

      {/* Headline */}
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-12 pb-14 md:pb-20">
        <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.85] text-6xl sm:text-7xl lg:text-[8.5rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className={`block ${i === 2 ? "text-primary" : "text-foreground"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md font-body text-base sm:text-lg text-secondary-foreground leading-relaxed">
            Heavy machinery, mine vehicles and every spare part in between. From filters to cranes —{" "}
            <span className="text-foreground font-semibold">we source it for you to own, not for hire.</span>
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-quote-btn"
              onClick={onQuote}
              className="inline-flex items-center h-14 px-8 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] font-semibold hover:bg-foreground transition-colors duration-200"
            >
              Request A Quote
            </button>
            <a
              href="#catalog"
              data-testid="hero-catalog-btn"
              className="inline-flex items-center gap-2 h-14 px-8 border border-border text-foreground font-mono text-xs uppercase tracking-[0.2em] hover:border-primary hover:text-primary transition-colors duration-200"
            >
              View Catalog <ArrowDown size={16} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
